import { X, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Task, Order, OrderStatus, Parcel, Address } from "@/api.types";
import { formatDistanceToNow, format } from "date-fns";
import { useWaypointById } from "@/api/waypoints";
import { useSelectedWaypointStore } from "./selected-waypoint.store";
import { Skeleton } from "@/components/ui/skeleton";

interface WaypointDetailsSidebarProps {
  onClose: () => void;
}

export function WaypointDetailsSidebar({
  onClose,
}: WaypointDetailsSidebarProps) {
  const { tripId, waypointId, clearSelectedWaypoint } =
    useSelectedWaypointStore();
  const {
    data: waypoint,
    isLoading,
    error,
  } = useWaypointById(tripId ?? "", waypointId ?? "");

  // Close handler that also clears the store
  const handleClose = () => {
    clearSelectedWaypoint();
    onClose();
  };

  // If no waypoint or trip ID is provided, don't render
  if (!tripId || !waypointId) return null;

  if (isLoading) {
    return (
      <div
        className="h-screen transition-all duration-300 ease-in-out z-10 shadow-lg border-r border-gray-200"
        style={{
          width: "400px",
          minWidth: "400px",
        }}
      >
        {/* Header Skeleton */}
        <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center">
            <Skeleton className="h-8 w-8 rounded-lg mr-2" />
            <Skeleton className="h-6 w-32" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Summary Skeleton */}
        <div className="p-4 bg-white border-b">
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-6 w-12" />
            </div>
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-6 w-12" />
            </div>
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-6 w-12" />
            </div>
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-6 w-32" />
            </div>
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>

        {/* Content Skeleton */}
        <div className="p-4">
          <div className="space-y-6">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex justify-between items-start">
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
                <div className="px-4 py-3 border-b">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <div className="px-4 py-3 border-b">
                  <Skeleton className="h-3 w-24 mb-3" />
                  <div className="space-y-2 pl-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                </div>
                <div className="p-4">
                  <Skeleton className="h-3 w-16 mb-3" />
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded border border-gray-100 p-3">
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !waypoint) {
    return (
      <div
        className="h-screen transition-all duration-300 ease-in-out z-10 shadow-lg border-r border-gray-200 flex items-center justify-center"
        style={{
          width: "400px",
          minWidth: "400px",
        }}
      >
        <div className="flex flex-col items-center p-6 text-center max-w-[80%]">
          <div className="text-red-500 mb-3">
            <AlertTriangle size={32} />
          </div>
          <p className="text-gray-700 font-medium text-lg mb-2">
            Failed to load waypoint details
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {error
              ? error instanceof Error
                ? error.message
                : "Unknown error"
              : "Waypoint not found"}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            className="mt-2"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Group tasks by order
  const tasksByOrder = waypoint.tasks.reduce(
    (acc, task) => {
      const orderId = task.order.id;
      if (!acc[orderId]) {
        acc[orderId] = {
          order: task.order,
          tasks: [],
        };
      }
      acc[orderId].tasks.push(task);
      return acc;
    },
    {} as Record<string, { order: Order; tasks: Task[] }>
  );

  // Calculate completion stats
  const completedTasksCount = waypoint.tasks.filter((task) =>
    ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status)
  ).length;
  const totalTasks = waypoint.tasks.length;
  const completionPercentage =
    totalTasks > 0 ? Math.round((completedTasksCount / totalTasks) * 100) : 0;

  // Count pickup and delivery tasks
  const pickupCount = waypoint.tasks.filter(
    (task) => task.type === "PICKUP"
  ).length;
  const deliveryCount = waypoint.tasks.filter(
    (task) => task.type === "DELIVERY"
  ).length;

  // Format address for display
  const formatAddress = (address: Address | undefined | null) => {
    if (!address) return "N/A";
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.province,
      address.postalCode,
      address.country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return `${format(date, "MMM d, yyyy h:mm a")} (${formatDistanceToNow(date, { addSuffix: true })})`;
    } catch (e) {
      return dateString;
    }
  };

  // Calculate total weight of parcels
  const getTotalWeight = (parcels: Parcel[]) => {
    if (!parcels || parcels.length === 0) return "N/A";
    const total = parcels.reduce(
      (sum, parcel) => sum + (parcel.weight || 0),
      0
    );
    return `${total.toFixed(2)} kg`;
  };

  // Get parcel counts by status
  const getParcelCounts = (parcels: Parcel[]) => {
    if (!parcels || parcels.length === 0) return "No parcels";
    const statuses = parcels.reduce(
      (acc, parcel) => {
        const status = parcel.status;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(statuses)
      .map(
        ([status, count]) =>
          `${count} ${status.toLowerCase().replace("_", " ")}`
      )
      .join(", ");
  };

  // Check if there are no orders
  const hasNoOrders = Object.keys(tasksByOrder).length === 0;

  return (
    <div
      className="h-screen transition-all duration-300 ease-in-out z-10 shadow-lg border-r border-gray-200"
      style={{
        width: "400px",
        minWidth: "400px",
      }}
    >
      <div className="h-full flex flex-col bg-gray-100 overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-semibold flex items-center">
              <span className="bg-gray-500 text-white w-8 h-8 flex items-center justify-center rounded-lg mr-2">
                {waypoint.sequence}
              </span>
              Waypoint Orders
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Waypoint summary */}
        <div className="p-4 bg-white border-b">
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <div className="text-sm font-medium mb-1">Total Orders</div>
              <div className="text-lg font-semibold">
                {Object.keys(tasksByOrder).length}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Total Tasks</div>
              <div className="text-lg font-semibold">{totalTasks}</div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Completion</div>
              <div className="text-lg font-semibold">
                {completionPercentage}%
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Task Types</div>
              <div className="flex gap-2 items-center">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                  {pickupCount} Pickups
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                  {deliveryCount} Deliveries
                </span>
              </div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto">
          {hasNoOrders ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <div className="text-gray-400 mb-3">
                <Package size={48} className="mx-auto opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No Orders Found
              </h3>
              <p className="text-gray-500 text-sm">
                This waypoint doesn't have any associated orders or tasks.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.values(tasksByOrder).map(({ order, tasks }) => (
                <div
                  key={order.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Order header */}
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold flex items-center mb-1">
                          <Package className="h-4 w-4 mr-2 text-gray-500" />
                          Order #{order.orderNumber}
                        </h3>
                        <div className="text-xs text-gray-500">
                          {formatDate(order.createdAt)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${getOrderStatusColor(order.status as OrderStatus)}`}
                        >
                          {order.status}
                        </span>
                        <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded-full">
                          {order.type}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order details */}
                  <div className="px-4 py-3 border-b">
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-500">
                          Parcels:
                        </span>{" "}
                        <span className="text-gray-700">
                          {order.parcels?.length || 0} items (
                          {getParcelCounts(order.parcels || [])})
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-500">
                          Total Weight:
                        </span>{" "}
                        <span className="text-gray-700">
                          {getTotalWeight(order.parcels || [])}
                        </span>
                      </div>
                      {order.appliedFees && order.appliedFees.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-500">
                            Total Fees:
                          </span>{" "}
                          <span className="text-gray-700">
                            $
                            {order.appliedFees
                              .reduce((sum, fee) => sum + (fee.amount || 0), 0)
                              .toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact details */}
                  <div className="px-4 py-3 border-b">
                    <div className="mb-3">
                      <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Sender Details
                      </h4>
                      <div className="text-sm space-y-1 pl-1">
                        <div className="font-medium">{order.senderName}</div>
                        <div className="text-gray-600">
                          {order.senderPhone || "No phone"}
                        </div>
                        <div className="text-gray-600">
                          {order.senderEmail || "No email"}
                        </div>
                        <div className="text-xs mt-1 text-gray-500">
                          {formatAddress(order.pickupAddress)}
                        </div>
                        {order.pickupNotes && (
                          <div className="text-xs mt-1 text-amber-600 bg-amber-50 p-2 rounded border border-amber-100">
                            Note: {order.pickupNotes}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Recipient Details
                      </h4>
                      <div className="text-sm space-y-1 pl-1">
                        <div className="font-medium">{order.recipientName}</div>
                        <div className="text-gray-600">
                          {order.recipientPhone || "No phone"}
                        </div>
                        <div className="text-gray-600">
                          {order.recipientEmail || "No email"}
                        </div>
                        <div className="text-xs mt-1 text-gray-500">
                          {formatAddress(order.deliveryAddress)}
                        </div>
                        {order.deliveryNotes && (
                          <div className="text-xs mt-1 text-amber-600 bg-amber-50 p-2 rounded border border-amber-100">
                            Note: {order.deliveryNotes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tasks */}
                  <div className="p-4">
                    <h4 className="text-xs font-semibold uppercase text-gray-500 mb-3">
                      Tasks
                    </h4>
                    <div className="space-y-3">
                      {tasks.length === 0 ? (
                        <div className="text-sm text-gray-500 italic text-center p-3 bg-gray-50 rounded">
                          No tasks found for this order
                        </div>
                      ) : (
                        tasks.map((task) => (
                          <div
                            key={task.id}
                            className="bg-gray-50 rounded border border-gray-100 overflow-hidden"
                          >
                            <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 flex justify-between items-center">
                              <div className="font-medium text-sm flex items-center">
                                <span
                                  className={`w-2 h-2 rounded-full mr-2 ${
                                    task.type === "PICKUP"
                                      ? "bg-blue-500"
                                      : "bg-purple-500"
                                  }`}
                                />
                                {task.type} #{task.taskNumber}
                              </div>
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${getTaskStatusColor(
                                  task.status
                                )}`}
                              >
                                {task.status}
                              </span>
                            </div>
                            <div className="p-3 text-sm space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <div className="text-xs text-gray-500">
                                    Created
                                  </div>
                                  <div>
                                    {formatDate(task.createdAt).split("(")[0]}
                                  </div>
                                </div>
                                {task.startedAt && (
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      Started
                                    </div>
                                    <div>
                                      {formatDate(task.startedAt).split("(")[0]}
                                    </div>
                                  </div>
                                )}
                                {task.completedAt && (
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      Completed
                                    </div>
                                    <div>
                                      {
                                        formatDate(task.completedAt).split(
                                          "("
                                        )[0]
                                      }
                                    </div>
                                  </div>
                                )}
                              </div>

                              {task.failureReason && (
                                <div className="mt-2 text-xs bg-red-50 p-2 rounded border border-red-100 text-red-600">
                                  <div className="font-medium">
                                    Failure Reason:
                                  </div>
                                  <div>{task.failureReason}</div>
                                  {task.failureReasonCode && (
                                    <div className="mt-1">
                                      Code: {task.failureReasonCode}
                                    </div>
                                  )}
                                </div>
                              )}

                              {task.proof && (
                                <div className="mt-2 text-xs">
                                  <div className="font-medium text-gray-600">
                                    Proof: {task.proof.mediaItems.length}{" "}
                                    photo(s)
                                  </div>
                                  {task.proof.notes && (
                                    <div className="mt-1 p-2 bg-gray-100 rounded">
                                      Note: {task.proof.notes}
                                    </div>
                                  )}
                                  {task.proof.mediaItems.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {task.proof.mediaItems.map((item) => (
                                        <a
                                          key={item.id}
                                          href={item.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block w-16 h-16 rounded overflow-hidden border border-gray-200"
                                        >
                                          <img
                                            src={item.url}
                                            alt="Proof"
                                            className="w-full h-full object-cover"
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getOrderStatusColor(status: OrderStatus): string {
  // Group statuses by color
  const greenStatuses = [OrderStatus.DELIVERED, OrderStatus.SORTED];

  const blueStatuses = [
    OrderStatus.IN_TRANSIT,
    OrderStatus.OUT_FOR_DELIVERY,
    OrderStatus.PICKUP_ASSIGNED,
    OrderStatus.PICKED_UP,
    OrderStatus.REGISTERED,
    OrderStatus.OUT_FOR_PICKUP,
    OrderStatus.DEPOT_DISPATCH_IN_PROGRESS,
    OrderStatus.DEPOT_RECEIVE_IN_PROGRESS,
    OrderStatus.DEPOT_SORT_IN_PROGRESS,
  ];

  const yellowStatuses = [
    OrderStatus.PICKUP_PENDING,
    OrderStatus.DEPOT_RECEIVE_PENDING,
    OrderStatus.DEPOT_SORT_PENDING,
    OrderStatus.DEPOT_DISPATCH_PENDING,
    OrderStatus.DELIVERY_PENDING,
    OrderStatus.AT_DEPOT,
    OrderStatus.RETURN_PENDING,
  ];

  const orangeStatuses = [OrderStatus.RETURN_IN_PROGRESS, OrderStatus.RETURNED];

  const redStatuses = [
    OrderStatus.PICKUP_FAILED,
    OrderStatus.DEPOT_RECEIVE_FAILED,
    OrderStatus.DEPOT_SORT_FAILED,
    OrderStatus.DEPOT_DISPATCH_FAILED,
    OrderStatus.DELIVERY_FAILED,
    OrderStatus.RETURN_FAILED,
  ];

  const grayStatuses = [
    OrderStatus.PICKUP_CANCELLED,
    OrderStatus.DEPOT_RECEIVE_CANCELLED,
    OrderStatus.DEPOT_SORT_CANCELLED,
    OrderStatus.DEPOT_DISPATCH_CANCELLED,
    OrderStatus.DELIVERY_CANCELLED,
    OrderStatus.RETURN_CANCELLED,
  ];

  if (greenStatuses.includes(status)) {
    return "bg-green-100 text-green-800";
  } else if (blueStatuses.includes(status)) {
    return "bg-blue-100 text-blue-800";
  } else if (yellowStatuses.includes(status)) {
    return "bg-yellow-100 text-yellow-800";
  } else if (orangeStatuses.includes(status)) {
    return "bg-orange-100 text-orange-800";
  } else if (redStatuses.includes(status)) {
    return "bg-red-100 text-red-800";
  } else if (grayStatuses.includes(status)) {
    return "bg-gray-100 text-gray-800";
  } else {
    return "bg-gray-100 text-gray-800";
  }
}

function getTaskStatusColor(
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED"
): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-800";
    case "IN_PROGRESS":
      return "bg-blue-100 text-blue-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    case "CANCELLED":
      return "bg-gray-100 text-gray-800";
    case "PENDING":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}
